/**
 * WEEX WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for WEEX.
 *
 * Subscribe format, channel names, and ping/pong mirror the production WEEX connector
 * (gitlab.1wq.net/mm/111-connman → connector/weex/market/ws/connector.go, common/{enum,helper}.go).
 *
 * @see wss://ws-spot.weex.com/v3/ws/public
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import { normalizeWeexMessage, WeexDepthSequence, weexPong } from './protocol';
import type {
  WeexDepthWsMessage,
  WeexTickerWsMessage,
  WeexTradesWsMessage,
  WeexWsStatus,
  WeexWsSubscribeRequest,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const WEEX_WS_BASE_URL = 'wss://ws-spot.weex.com/v3/ws/public';

/**
 * WEEX public WS channel prefixes.
 * Streams use <symbol>@ticker, <symbol>@depth<level>, and <symbol>@trade.
 */
export const WEEX_WS_TICKER_PREFIX = 'ticker';
export const WEEX_WS_DEPTH_PREFIX = 'depth';
export const WEEX_WS_TRADES_PREFIX = 'trade';

/**
 * Order book depth level for WS subscriptions.
 * WEEX v3 public WS only supports levels 15 and 200.
 */
export const WEEX_WS_DEPTH_LEVEL = 15;

/**
 * WEEX WebSocket channel descriptors.
 * @see connector/weex/common/enum.go
 */
export const WEEX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  orderbook: {
    channel: 'orderbook',
    topic: 'depth',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
  ticker: {
    channel: 'ticker',
    topic: 'ticker',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
  trades: {
    channel: 'trades',
    topic: 'trades',
    supportsBatching: false,
    docUrl: 'https://api-spot.weex.com',
  },
};

/**
 * WEEX WebSocket topology information.
 * V3 supports stream arrays and application-level PONG.
 */
export const WEEX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: WEEX_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 20_000,
  note: 'V3 SUBSCRIBE uses params streams such as BTCUSDT@ticker; reply to ping with method PONG.',
};

// ========================================
// Channel builders
// ========================================

export const weexTickerChannel = (symbol: string): string => `${symbol}@${WEEX_WS_TICKER_PREFIX}`;

export const weexDepthChannel = (symbol: string, level: number = WEEX_WS_DEPTH_LEVEL): string =>
  `${symbol}@${WEEX_WS_DEPTH_PREFIX}${level}`;

export const weexTradesChannel = (symbol: string): string => `${symbol}@${WEEX_WS_TRADES_PREFIX}`;

// ========================================
// WebSocket Subscription Functions
// ========================================

/**
 * Creates a WEEX WebSocket subscription for the given channel.
 * WEEX subscribes one channel per message and uses event-based ping/pong.
 */
function createWeexWsSubscription<T>(params: {
  channel: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { channel, onMessage, onError, onStatusChange } = params;

  const ws = new ReconnectingWebSocket(WEEX_WS_BASE_URL, [], {
    maxRetries: 10,
    minReconnectionDelay: 1000,
    maxReconnectionDelay: 30000,
    reconnectionDelayGrowFactor: 1.5,
    connectionTimeout: 10000,
  });

  const depthSequence = new WeexDepthSequence();
  ws.addEventListener('open', () => {
    depthSequence.reset();
    onStatusChange?.('connected');

    // Subscribe this handler to its v3 stream.
    const subscribeMessage: WeexWsSubscribeRequest = {
      method: 'SUBSCRIBE',
      params: [channel],
      id: 1,
    };
    ws.send(JSON.stringify(subscribeMessage));
  });

  ws.addEventListener('close', () => {
    onStatusChange?.('disconnected');
  });

  ws.addEventListener('error', () => {
    onStatusChange?.('error');
    onError?.(new Error('WebSocket connection error'));
  });

  ws.addEventListener('message', event => {
    try {
      const message = JSON.parse(event.data);

      const pong = weexPong(message);
      if (pong) {
        ws.send(JSON.stringify(pong));
        return;
      }
      if (message.result === false) {
        onError?.(new Error(message.msg ?? 'WEEX subscription rejected'));
        return;
      }
      if (message.s && message.s !== channel.split('@')[0]) return;
      const continuity = depthSequence.accept(message);
      if (continuity === 'gap') ws.reconnect();
      if (continuity !== 'accept') return;
      const decoded = normalizeWeexMessage(message, WEEX_WS_DEPTH_LEVEL);
      if (decoded?.channel === channel) onMessage(decoded as T);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to parse message'));
    }
  });

  return ws;
}

/**
 * Subscribe to WEEX orderbook (depth) WebSocket.
 */
export function subscribeWeexOrderbook(params: {
  symbol: string;
  onMessage: (data: WeexDepthWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexDepthWsMessage>({
    channel: weexDepthChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to WEEX ticker WebSocket.
 */
export function subscribeWeexTicker(params: {
  symbol: string;
  onMessage: (data: WeexTickerWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexTickerWsMessage>({
    channel: weexTickerChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to WEEX public trades WebSocket.
 */
export function subscribeWeexTrades(params: {
  symbol: string;
  onMessage: (data: WeexTradesWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createWeexWsSubscription<WeexTradesWsMessage>({
    channel: weexTradesChannel(symbol),
    onMessage,
    onError,
    onStatusChange,
  });
}

/** Subscribe to native v3 candles; tuple DTO includes quote volume and close time. */
export function subscribeWeexKlines(params: {
  symbol: string;
  interval: string;
  onMessage: (data: { data: Array<Array<string | number>> }) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: WeexWsStatus) => void;
}): ReconnectingWebSocket {
  return createWeexWsSubscription({
    ...params,
    channel: `${params.symbol}@kline_${params.interval}_LAST_PRICE`,
  });
}
